import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 1000px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;
    align-items: center;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(9, auto);
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
    display: block;
    height: auto;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'HTML & CSS',
    'Tailwind',
    'Bootstrap',
    'JavaScript',
    'Vue.js',
    'React.js',

    'Next.js',
    'TypeScript',
    'Node.js',
    'Express.js',
    'Python',
    'FastAPI',

    'PHP',
    'Laravel',
    'Prisma',
    'MySQL',
    'PostgreSQL',
    'Supabase',
    'MongoDB',

    'NeonDB',
    'Git',
    'Figma',
    'LLM',
    'LangChain',
    'RAG',
    'Pinecone',
    'HuggingFace',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              My name is Rizky (you can call me Tama), and I enjoy creating things that live on the
              internet. My interest in web development started back in 2014 when I decided to try
              editing custom Blog at{' '}
              <a href="https://kaskus.co.id/" target="_blank" rel="noreferrer">
                Kaskus
              </a>{' '}
              — turns out hacking together a custom reblog button taught me a lot about HTML &amp;
              CSS!
            </p>

            <p>
              Fast-forward to today, I thrive in cross-functional teams, translating business needs
              into clean code and smooth user experiences. My focus these days is on building
              accessible, inclusive, and impactful products. Currently, I’m seeking remote
              opportunities with global teams to create technology that empowers people to work from
              anywhere — combining my full-stack expertise with growing skills in AI-driven
              solutions.
            </p>

            <p>Here are a few technologies I’ve been working with:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.png"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
